import {
	ContainerPadding,
	CTA,
	Spinner,
	Text,
	TextAlignment,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import { Checkmark, VehicleIdBuzz } from '@volkswagen-onehub/icons-core';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { ConsentimientosSection } from '../../../components/inputs-group/consentimientos-section';
import { EnvioSection } from '../../../components/inputs-group/envio-section';
import { CodigoPostalInput } from '../../../components/inputs-group/inputs/codigo-postal-input';
import { ComentarioInput } from '../../../components/inputs-group/inputs/comentario-input';
import { CtasRadio } from '../../../components/inputs-group/inputs/core/ctas-radio';
import { OptionCard } from '../../../components/inputs-group/inputs/core/option-card';
import { SelectExtended } from '../../../components/inputs-group/inputs/core/select-extended';
import { TextInputExtended } from '../../../components/inputs-group/inputs/core/text-input-extended';
import { DniInput } from '../../../components/inputs-group/inputs/dni-input';
import { EmailInput } from '../../../components/inputs-group/inputs/email-input';
import { FechaInput } from '../../../components/inputs-group/inputs/fecha-input';
import { LettersInput } from '../../../components/inputs-group/inputs/letters-input';
import { PaisMatriculaInput } from '../../../components/inputs-group/inputs/pais-matricula-input';
import { phonePrefixes, TelefonoInput } from '../../../components/inputs-group/inputs/telefono-input';
import { TratoNombreInput } from '../../../components/inputs-group/inputs/trato-nombre-input';
import { LegalesSection } from '../../../components/inputs-group/legales-section';
import { CardsGroup } from '../../../components/screen/cards-group';
import { ErrorSection } from '../../../components/screen/error';
import { InputsGroup } from '../../../components/screen/inputs-group';
import { Section } from '../../../components/screen/section';
import { ThankyouScreen } from '../../../components/screen/thankyou';
import { FIELD_NAME, FORM_SUBMIT_STATUS } from '../../../constants/form-constants';
import { useApp } from '../../../contexts/app-context';
import { personEntity } from '../../../entities/person-entity';
import { formService } from '../../../form-service';
import {
	fetchYumiwiTheOriginalsAttendee,
	fetchYumiwiTheOriginalsMember,
	fetchYumiwiTheOriginalsPlateCheck,
} from '../../../service/api/fetch/yumiwi-fetch';
import {
	getBrands,
	getCarlineName,
	getCountries,
	getCountryCode,
	getCountryName,
	getModels,
	getValidModel,
} from './helper';
import { logoFurgoConcentracion2026 } from './logo-furgo-concentracion';

interface MiFurgo {
	matricula: string;
	modelo?: string;
	year?: string | number;
}

export const TheOriginalsFest2026Form = () => {
	const { setAppThankyouScreen } = useApp();

	const TRUE = 'si';
	const FALSE = 'no';

	const CAMPING_URL = 'https://booking.ballena-alegre.com/search?promotionCode=FurgoVW26&lang=es';

	const getFormData = async () => {
		/****************************
		 * Eliminar campos innecesarios para poder validar form
		 ****************************/
		if (!showVoyConVW()) {
			formService.clearField('voyConVW');
		}
		if (!showSoyTitular()) {
			formService.clearField('soyTitular');
		}
		if (!showSoyMiembro()) {
			formService.clearField('soyMiembro');
		}
		if (!showDatosMiembro()) {
			formService.clearField('dniMiembro');
			formService.clearField('numeroMiembro');
		} else {
			formService.clearField(FIELD_NAME.DNI);
		}
		if (!showSeleccionFurgo()) {
			formService.clearField('furgoMiembro');
		}
		if (!showDatosFurgo()) {
			formService.clearField('brand');
			formService.clearField('model');
			formService.clearField('matricula');
			formService.clearField('furgoVW');
			formService.clearField('year');
		}
		if (!showMiHistoria()) {
			formService.clearField('miHistoria');
		}

		//Cortamos la ejecucion si el form es invalido
		if (!formService.isFormValid()) return;

		/****************************
		 * Preparar formData para envio Yumiwi
		 ****************************/

		const formData = new FormData();

		const furgoMiembro = formService.getFieldValue('furgoMiembro');
		const furgoVW = getCarlineName(formService.getFieldValue('furgoVW'));

		const marca = getCarlineName(formService.getFieldValue('brand')) || 'VOLKSWAGEN';
		const modelo = formService.getFieldValue('model') || furgoVW || furgoMiembro?.modelo || '';
		const matricula = formService.getFieldValue(FIELD_NAME.PLATE) || furgoMiembro?.matricula || '';
		const year = String(parseInt(formService.getFieldValue('year') || furgoMiembro?.year || '0', 10));

		const ticketType = formService.getFieldValue('dniMiembro')
			? 'THE ORIGINALS'
			: soyTitular === TRUE
				? marca === 'VOLKSWAGEN'
					? 'FURGO VW'
					: 'FURGO'
				: 'FURGOLOVER';

		formData.append('first_name', personEntity.name || '');
		formData.append('last_name', personEntity.surname || '');
		formData.append('email', personEntity.email || '');
		formData.append('phone', personEntity.phone || '');
		formData.append('nid', personEntity.dni || formService.getFieldValue('dniMiembro') || '');
		formData.append('birth_date', personEntity.birthday || '');
		formData.append('postal_code', personEntity.postalCode || '');
		formData.append('locator', formService.getFieldValue('numeroMiembro') || '');
		formData.append('ticket_type', ticketType);

		// Extra fields
		formData.append('extra[lived_history]', formService.getFieldValue('miHistoria') || '');
		formData.append('extra[country]', getCountryName(personEntity.country || 'ES'));
		formData.append('extra[province]', personEntity.province || '');
		formData.append('extra[city]', personEntity.city || '');
		formData.append('extra[second_last_name]', personEntity.secondSurname || '');
		formData.append('extra[accept_terms_and_conditions]', 'true');
		formData.append('extra[legal_image]', 'true');
		formData.append(
			'extra[legal_lssi]',
			formService.getFieldValue(FIELD_NAME.RGPD_COMUNICACIONES_COMERCIALES) == 'PERMITE' ? 'true' : 'false',
		);
		formData.append(
			'extra[legal_gdpr]',
			formService.getFieldValue(FIELD_NAME.RGPD_PERFILADO) == 'PERMITE' ? 'true' : 'false',
		);

		if (showSeleccionFurgo() || showDatosFurgo()) {
			// External fields
			formData.append('external[carline][marca]', marca);
			formData.append('external[carline][modelo]', modelo);
			formData.append('external[carline][matricula]', matricula);
			formData.append('external[carline][year]', year);
		}

		const response = await fetchYumiwiTheOriginalsAttendee(formData);

		if (!response?.content?.content?.success) {
			formService.setFormSubmitStatus(FORM_SUBMIT_STATUS.ERROR);
			if (response?.content?.content?.message?.startsWith('Conflict')) {
				setErrorEnvioMessage('El número The Originals que ingresaste ya está registrado en FurgoVolkswagen 2026.');
			}
			return; // No enviamos DPS
		}

		/****************************
		 * Preparar data para envio Ndps
		 ****************************/

		const data = {
			'headers[DPS_TOKEN]': 'LCV_DDB_FRM_FURGOCONCENTRACION',
			'headers[FORM_OBJECTIVE]': 'Evento',
			'headers[FORM_DETAILS]': 'FurgoVolkswagen 2026',
			'headers[SF_CAMPAIGN_NAME]': 'Web Formulario Inscripcion THOF 2026',

			'flags[PERSON_RGPD_ADAPTED]': true,
			//'flags[FLAG_IS_MYSTERY_LEAD]': false,
			//'flags[FLAG_RELACION_MARCA_SIN_IC]': false,
			//'flags[FLAG_SF_INTEGRATION]': true,
			//'flags[FLAG_ALLOWS_DATA_ENRICHMENT]': true,
			//'flags[FLAG_IS_COMPANY]': false,

			'fields[DPS_BRAND]': 'Volkswagen CV',
			'fields[EVENT_NAME]': 'FurgoVolkswagen 2026',
			'fields[SF_LEAD_RECORDTYPE_ID]': '0121i000000D7NyAAK',
			'fields[SF_LEAD_RECORDTYPE_NAME]': undefined,
			'fields[SF_LEAD_SALES_INITIAL_ACTION]': 'Prospect',

			'fields[PERSON_TREATMENT]': formService.getFieldValue(FIELD_NAME.TREATMENT),
			'fields[PERSON_NAME]': formService.getFieldValue(FIELD_NAME.NAME),
			'fields[PERSON_SURNAME_1]': personEntity.surname,
			'fields[PERSON_SURNAME_2]': personEntity.secondSurname,
			'fields[PERSON_EMAIL]': personEntity.email,
			'fields[PERSON_PHONE_MOBILE]': personEntity.phone,
			'fields[PERSON_PHONE_MOBILE_FULL]': personEntity.phonePrefix + personEntity.phone,
			//'fields[PERSON_PHONE_PREFIX]': fields.prefix,
			'fields[PERSON_DNI_NIF]': personEntity.dni || formService.getFieldValue('dniMiembro'),
			'fields[PERSON_BIRTHDATE]': dayjs(personEntity.birthday).format('DD/MM/YYYY'),
			'fields[PERSON_POSTAL_CP]': personEntity.postalCode,

			'fields[VEH_ACTUAL_BRAND]': showSeleccionFurgo() || showDatosFurgo() ? marca : undefined,
			'fields[VEH_ACTUAL_MODEL]': showSeleccionFurgo() || showDatosFurgo() ? getValidModel(modelo) : undefined,
			'fields[VEH_ACTUAL_PLATE]': showSeleccionFurgo() || showDatosFurgo() ? matricula : undefined,

			'fields[SURVEY_ANSWER_01]': formService.getFieldValue('voyConFurgo') === TRUE,
			'fields[SURVEY_ANSWER_02]': formService.getFieldValue('voyConVW') === TRUE,
			'fields[SURVEY_ANSWER_03]': 'PERMITE',
			'fields[SURVEY_ANSWER_04]': personEntity.country,
			//'fields[SURVEY_ANSWER_05]': 'PERMITE',
			'fields[SURVEY_ANSWER_06]': formService.getFieldValue('miHistoria'),
		};

		return data;
	};

	const CTA_WIDTH = 180; // px
	const MARGIN_SECTIONS = ContainerPadding.static530; // 80px
	const SCROLL_OFFSET = 80; // px

	const voyConVWRef = useRef<HTMLDivElement>(null);
	const soyTitularRef = useRef<HTMLDivElement>(null);
	const soyMiembroRef = useRef<HTMLDivElement>(null);
	const datosMiembroRef = useRef<HTMLDivElement>(null);
	const seleccionFurgoRef = useRef<HTMLDivElement>(null);
	const datosFurgoRef = useRef<HTMLDivElement>(null);
	const miHistoriaRef = useRef<HTMLDivElement>(null);
	const datosPersonalesRef = useRef<HTMLDivElement>(null);

	const [voyConFurgo, setVoyConFurgo] = useState<string | undefined>(
		formService.getFieldValue('voyConFurgo') as string,
	);
	const [voyConVW, setVoyConVW] = useState<string | undefined>(formService.getFieldValue('voyConVW') as string);
	const [soyTitular, setSoyTitular] = useState<string | undefined>(formService.getFieldValue('soyTitular') as string);
	const [soyMiembro, setSoyMiembro] = useState<string | undefined>(formService.getFieldValue('soyMiembro') as string);

	const [foundMiembro, setFoundMiembro] = useState(false);
	const [furgoInscrita, setFurgoInscrita] = useState(false);
	const [furgoSelected, setFurgoSelected] = useState<MiFurgo | null>(null);
	const [otraFurgoSelected, setOtraFurgoSelected] = useState(false);
	const [misFurgos, setMisFurgos] = useState<MiFurgo[] | null>(null);
	const [disabledMatriculas, setDisabledMatriculas] = useState<string[]>([]);
	const [enabledMatriculas, setEnabledMatriculas] = useState<string[]>([]);
	const [loadingFurgo, setLoadingFurgo] = useState<string | null>(null);
	const [errorEnvioMessage, setErrorEnvioMessage] = useState<string | undefined>(undefined);

	useEffect(() => {
		const unsubscribeVoyConFurgo = formService.subscribeToField('voyConFurgo', {
			onChange: onChangeVoyConFurgo,
		});
		const unsubscribeVoyConVW = formService.subscribeToField('voyConVW', {
			onChange: onChangeVoyConVW,
		});
		const unsubscribeSoyTitular = formService.subscribeToField('soyTitular', {
			onChange: onChangeSoyTitular,
		});
		const unsubscribeSoyMiembro = formService.subscribeToField('soyMiembro', {
			onChange: onChangeSoyMiembro,
		});

		return () => {
			unsubscribeVoyConFurgo();
			unsubscribeVoyConVW();
			unsubscribeSoyTitular();
			unsubscribeSoyMiembro();
		};
	}, []);

	/****************************
	 * LOGICA ONCHANGE
	 ****************************/

	const scrollToElement = (elementRef: React.RefObject<HTMLDivElement>) => {
		const getScrollParent = (node: HTMLElement): HTMLElement | Window => {
			let parent = node.parentElement;

			while (parent) {
				const { overflowY } = window.getComputedStyle(parent);
				const canScroll = /(auto|scroll|overlay)/.test(overflowY) && parent.scrollHeight > parent.clientHeight;
				if (canScroll) return parent;
				parent = parent.parentElement;
			}

			return window;
		};
		const isWindowContainer = (container: HTMLElement | Window): container is Window => container === window;

		setTimeout(() => {
			const element = elementRef.current;
			if (!element) return;

			const scrollParent = getScrollParent(element);

			if (isWindowContainer(scrollParent)) {
				const top = element.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
				window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
				return;
			}

			const parentRect = scrollParent.getBoundingClientRect();
			const elementRect = element.getBoundingClientRect();
			const top = scrollParent.scrollTop + (elementRect.top - parentRect.top) - SCROLL_OFFSET;

			scrollParent.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
		}, 100);
	};

	const onChangeVoyConFurgo = (value: string | undefined) => {
		setVoyConFurgo(value);
		formService.setFieldValue('voyConVW', undefined);
		formService.setFieldValue('soyTitular', undefined);
		formService.setFieldValue('soyMiembro', undefined);
		if (value === TRUE) {
			scrollToElement(voyConVWRef);
		} else {
			scrollToElement(datosPersonalesRef);
		}
	};

	const onChangeVoyConVW = (value: string | undefined) => {
		setVoyConVW(value);
		const soyTitular = formService.getFieldValue('soyTitular');
		const soyMiembro = formService.getFieldValue('soyMiembro');
		if (soyTitular) {
			if (soyMiembro === TRUE) {
				scrollToElement(seleccionFurgoRef);
			} else {
				scrollToElement(datosFurgoRef);
			}
		} else {
			scrollToElement(soyTitularRef);
		}
	};

	const onChangeSoyTitular = (value: string | undefined) => {
		setSoyTitular(value);
		formService.setFieldValue('soyMiembro', undefined);
		if (value === TRUE) {
			scrollToElement(soyMiembroRef);
		} else {
			scrollToElement(datosPersonalesRef);
		}
	};

	const onChangeSoyMiembro = (value: string | undefined) => {
		setSoyMiembro(value);
		if (value === TRUE) {
			scrollToElement(datosMiembroRef);
		} else {
			scrollToElement(datosFurgoRef);
		}
	};

	/****************************
	 * LOGICA MOSTRAR SECTIONS
	 ****************************/

	const showVoyConVW = () => {
		return voyConFurgo === TRUE;
	};

	const showSoyTitular = () => {
		return voyConFurgo === TRUE && voyConVW;
	};

	const showSoyMiembro = () => {
		return voyConFurgo === TRUE && voyConVW === TRUE && soyTitular === TRUE;
	};

	const showDatosMiembro = () => {
		return showSoyMiembro() && soyMiembro === TRUE;
	};

	const showSeleccionFurgo = () => {
		return showDatosMiembro() && misFurgos;
	};

	const showDatosFurgo = () => {
		if (showSoyMiembro() && otraFurgoSelected) return true; // Miembro y otra furgo
		if (!showSoyMiembro() && showSoyTitular() && soyTitular === TRUE) return true; // No miembro y titular
		if (showSoyMiembro() && soyMiembro === FALSE) return true; // Miembro
		return false;
	};

	const showMiHistoria = () => {
		if (voyConFurgo === FALSE) return false; // Voy sin furgo
		if (soyTitular === FALSE) return false; // Soy acompañante
		if (furgoSelected) return true;
		if (showSeleccionFurgo() && otraFurgoSelected && voyConVW === TRUE) return true;
		if (showSeleccionFurgo() && (!furgoSelected || !otraFurgoSelected)) return false;
		if (soyMiembro === TRUE && voyConVW === TRUE && foundMiembro) return true;
		if (soyMiembro === FALSE && voyConVW === TRUE) return true;
		return false;
	};

	const showDatosPersonales = () => {
		if (showMiHistoria() && !miHistoriaAnswered) return false;
		if (showDatosFurgo()) return true; // Siempre que se muestre datos furgo
		if (voyConFurgo === FALSE) return true; // Voy sin furgo
		if (soyTitular === FALSE) return true; // Soy acompañante
		if (showDatosMiembro() && foundMiembro && miHistoriaAnswered) return true;
		if (voyConFurgo && voyConVW && soyTitular && soyMiembro === FALSE) return true;
		return false;
	};

	/****************************
	 * HEADER
	 ****************************/

	const sectionHeader = (
		<Section disableScrollFocus>
			{logoFurgoConcentracion2026}
			<Text bold appearance={TokenTextAppearance.headline300}>
				Tu acceso al evento empieza aquí
			</Text>
			<Text appearance={TokenTextAppearance.headline200}>
				Solo necesitamos saber si vendrás con furgo, si eres parte de The Originals y algunos de tus datos personales.
				Así podremos preparar tu llegada… ¡y también tus sorpresas!
			</Text>
		</Section>
	);

	/****************************
	 * VENDRAS CON FURGO
	 ****************************/

	const sectionVoyConFurgo = (
		<Section disableScrollFocus paddingTop={MARGIN_SECTIONS}>
			<Text bold appearance={TokenTextAppearance.headline200}>
				¿Vendrás con furgo?
			</Text>
			<CtasRadio
				name="voyConFurgo"
				options={[
					{ text: 'Sí', value: TRUE },
					{ text: 'No', value: FALSE },
				]}
				width={CTA_WIDTH + 'px'}
				required
			/>
		</Section>
	);

	/****************************
	 * VENDRAS CON VW
	 ****************************/

	const sectionVoyConVW = (
		<div ref={voyConVWRef} hidden={!showVoyConVW()}>
			<Section disableScrollFocus paddingTop={MARGIN_SECTIONS}>
				<Text bold appearance={TokenTextAppearance.headline200}>
					¿Es una furgo Volkswagen?
				</Text>
				<CtasRadio
					name="voyConVW"
					options={[
						{ text: 'Sí', value: TRUE },
						{ text: 'No', value: FALSE },
					]}
					width={CTA_WIDTH + 'px'}
					required
				/>
			</Section>
		</div>
	);

	/****************************
	 * ERES TITULAR
	 ****************************/

	const sectionSoyTitular = (
		<div ref={soyTitularRef} hidden={!showSoyTitular()}>
			<Section disableScrollFocus paddingTop={MARGIN_SECTIONS}>
				<Text bold appearance={TokenTextAppearance.headline200}>
					¿Eres el titular de la furgo o acompañante?
				</Text>
				<Text appearance={TokenTextAppearance.copy200}>
					¡Importante! Solo una persona por furgo podrá inscribirse como titular del vehículo. Si venís varios en la
					misma, solo uno debe seleccionar esta opción.
				</Text>
				<CtasRadio
					name="soyTitular"
					options={[
						{ text: 'Soy titular', value: TRUE },
						{ text: 'Soy acompañante', value: FALSE },
					]}
					width={CTA_WIDTH + 'px'}
					required
				/>
				<Text appearance={TokenTextAppearance.copy100} color={TokenTextColor.secondary}>
					En caso de seleccionar “Soy titular", los datos deberán coincidir con el propietario o conductor habitual del
					vehículo. Al continuar, declaras bajo tu responsabilidad que eres el titular legal del vehículo indicado y que
					estás realizando esta inscripción con tus propios datos. La organización podrá solicitar acreditación
					documental en el acceso al evento.
				</Text>
			</Section>
		</div>
	);

	/****************************
	 * ERES MIEMBRO
	 ****************************/

	const sectionSoyMiembro = (
		<div ref={soyMiembroRef} hidden={!showSoyMiembro()}>
			<Section disableScrollFocus paddingTop={MARGIN_SECTIONS}>
				<Text bold appearance={TokenTextAppearance.headline200}>
					¿Estás registrado en The Originals?
				</Text>
				<CtasRadio
					name="soyMiembro"
					options={[
						{ text: 'Sí', value: TRUE },
						{ text: 'No', value: FALSE },
					]}
					width={CTA_WIDTH + 'px'}
					required
				/>
				<Text appearance={TokenTextAppearance.copy100} color={TokenTextColor.secondary}>
					Recuerda que por ser The Originals disfrutarás de ventajas exclusivas durante el evento.
				</Text>
			</Section>
		</div>
	);

	/****************************
	 * DATOS MIEMBRO
	 ****************************/

	const [sendingRequestMiembro, setSendingRequestMiembro] = useState(false);
	const [sentRequestMiembro, setSentRequestMiembro] = useState(false);
	const requestMiembro = async () => {
		const numeroMiembro = formService.getFieldValue('numeroMiembro');
		const dniMiembro = formService.getFieldValue('dniMiembro');

		if (!dniMiembro) {
			formService.setFieldError('dniMiembro', true);
		}
		if (!numeroMiembro) {
			formService.setFieldError('numeroMiembro', true);
		}
		if (!numeroMiembro || !dniMiembro) return;

		setSendingRequestMiembro(true);
		const response = await fetchYumiwiTheOriginalsMember(dniMiembro, numeroMiembro);
		setSendingRequestMiembro(false);
		setSentRequestMiembro(true);

		if (response?.content?.content?.success) {
			setFoundMiembro(true);
			scrollToElement(seleccionFurgoRef);

			const person = response?.content?.content?.data;

			formService.setFieldValue(FIELD_NAME.NAME, person.first_name);
			formService.setFieldValue(FIELD_NAME.SURNAME_1, person.last_name);
			formService.setFieldValue(FIELD_NAME.EMAIL, person.email);
			// Necesitamos partir person.phone en prefix y phone
			const foundPrefix = phonePrefixes.find((prefix) => person.phone?.startsWith(prefix));
			if (foundPrefix) {
				formService.setFieldValue(FIELD_NAME.PHONE_PREFIX, foundPrefix);
				formService.setFieldValue(FIELD_NAME.PHONE, person.phone.substring(foundPrefix.length));
			} else {
				formService.setFieldValue(FIELD_NAME.PHONE_PREFIX, '+34');
				formService.setFieldValue(FIELD_NAME.PHONE, person.phone);
			}
			formService.setFieldValue(FIELD_NAME.DNI, person.nid);
			formService.setFieldValue(FIELD_NAME.BIRTHDAY, person.birth_date);
			formService.setFieldValue(FIELD_NAME.CP, person?.postal_code);
			formService.setFieldValue(FIELD_NAME.CITY, person.extra?.city);
			formService.setFieldValue(FIELD_NAME.PROVINCE, person.extra?.province);
			formService.setFieldValue(FIELD_NAME.COUNTRY, getCountryCode(person.extra?.country) || 'ES');

			setMisFurgos(person.external?.carlines);
		} else {
			setFoundMiembro(false);
		}
	};

	const sectionDatosMiembro = (
		<div ref={datosMiembroRef} hidden={!showDatosMiembro()}>
			<Section disableScrollFocus paddingTop={ContainerPadding.static200}>
				<InputsGroup>
					<DniInput
						name="dniMiembro"
						types={['DNI', 'NIE']}
						required
						disabled={sendingRequestMiembro || foundMiembro}
					/>
					<TextInputExtended
						label="Número The Originals"
						name="numeroMiembro"
						validate={(value) => /^TH\d{6}$/i.test(value)}
						maxLength={8}
						errorMessage="Número incorrecto"
						required
						disabled={sendingRequestMiembro || foundMiembro}
						onChange={(e: any) => {
							e.target.value = e.target.value.toUpperCase();
						}}
					/>
				</InputsGroup>
				<div style={{ width: CTA_WIDTH + 'px' }} className="cta primary">
					<CTA
						tag="button"
						emphasis="primary"
						onClick={requestMiembro}
						type="button"
						stretchContent
						size="small"
						disabled={sendingRequestMiembro}
						icon={
							sentRequestMiembro && foundMiembro ? (
								<Checkmark />
							) : sendingRequestMiembro ? (
								<Spinner variant="default" />
							) : undefined
						}
						tabIndex={0}
					>
						{sentRequestMiembro && foundMiembro ? 'Aceptado' : sendingRequestMiembro ? 'Cargando...' : 'Continuar'}
					</CTA>
				</div>
			</Section>

			{sentRequestMiembro && !foundMiembro ? (
				<ErrorSection paddingTop={ContainerPadding.static200} align="left">
					Los datos no coinciden con nuestro registro de The Originals. Verifica tu DNI y número de socio. Si necesitas
					ayuda, contáctanos.
				</ErrorSection>
			) : undefined}
		</div>
	);

	/****************************
	 * SELECTOR DE MI FURGO
	 ****************************/

	const isSelectedFurgo = (furgo: MiFurgo) => {
		return furgoSelected?.matricula === furgo?.matricula;
	};

	const handleSelectFurgo = async (furgo: MiFurgo) => {
		if (disabledMatriculas.includes(furgo.matricula)) {
			return;
		}

		// Si ya está verificada como habilitada, seleccionar directamente
		if (enabledMatriculas.includes(furgo.matricula)) {
			setFurgoInscrita(false);
			setOtraFurgoSelected(false);
			setFurgoSelected(furgo);
			formService.setFieldValue('furgoMiembro', furgo ?? '');
			scrollToElement(miHistoriaRef);
			return;
		}

		setLoadingFurgo(furgo.matricula);
		const response = await fetchYumiwiTheOriginalsPlateCheck(furgo.matricula);
		setLoadingFurgo(null);

		setFurgoInscrita(false);
		setOtraFurgoSelected(false);
		formService.setFieldError(FIELD_NAME.PLATE, false);
		if (response?.content?.content?.success) {
			setEnabledMatriculas((prev) => [...prev, furgo.matricula]);
			setFurgoSelected(furgo);
			formService.setFieldValue('furgoMiembro', furgo ?? '');
			scrollToElement(miHistoriaRef);
		} else {
			setFurgoInscrita(true);
			formService.setFieldError(FIELD_NAME.PLATE, true);
			setDisabledMatriculas((prev) => [...prev, furgo.matricula]);
		}
	};

	const handleSelectOtraFurgo = () => {
		setOtraFurgoSelected(true);
		setFurgoSelected(null);
		formService.setFieldValue('furgoMiembro', '');
		scrollToElement(datosFurgoRef);
	};

	const sectionSeleccionFurgo = (
		<div ref={seleccionFurgoRef} hidden={!showSeleccionFurgo()}>
			<Section disableScrollFocus paddingTop={MARGIN_SECTIONS}>
				<Text bold appearance={TokenTextAppearance.headline200}>
					¿Con qué furgo vendrás a la concentración?
				</Text>
				<CardsGroup paddingBottom={ContainerPadding.static300}>
					{misFurgos &&
						misFurgos.map((furgo) => (
							<OptionCard
								key={furgo.matricula}
								title={furgo.modelo?.toUpperCase() || ''}
								text={furgo.matricula}
								icon={loadingFurgo === furgo.matricula ? <Spinner variant="default" /> : <VehicleIdBuzz />}
								isActive={isSelectedFurgo(furgo)}
								disabled={disabledMatriculas.includes(furgo.matricula) || loadingFurgo === furgo.matricula}
								onSelect={() => handleSelectFurgo(furgo)}
							/>
						))}
					<OptionCard
						key="OTRA FURGO"
						title="OTRA FURGO"
						text={''}
						icon={<VehicleIdBuzz />}
						isActive={otraFurgoSelected}
						onSelect={() => handleSelectOtraFurgo()}
					/>
				</CardsGroup>

				{furgoInscrita ? (
					<ErrorSection paddingTop={ContainerPadding.static200} align="left">
						Esta furgo ya se ha inscrito con otro asistente. Si eres un acompañante, selecciona esa opción, por favor.
					</ErrorSection>
				) : undefined}
			</Section>
		</div>
	);

	/****************************
	 * DATOS DE OTRA FURGO
	 ****************************/

	const handlePlateBlur = async () => {
		const matricula = formService.getFieldValue(FIELD_NAME.PLATE);
		if (!matricula || !formService.isFieldValid(FIELD_NAME.PLATE)) return;

		const response = await fetchYumiwiTheOriginalsPlateCheck(matricula);
		if (response?.content?.content?.success) {
			setFurgoInscrita(false);
		} else {
			setFurgoInscrita(true);
			formService.setFieldError(FIELD_NAME.PLATE, true);
		}
	};

	const sectionDatosFurgo = (
		<div ref={datosFurgoRef} hidden={!showDatosFurgo()}>
			<Section disableScrollFocus paddingTop={MARGIN_SECTIONS}>
				<Text bold appearance={TokenTextAppearance.headline200}>
					Por favor, indícanos los datos de la furgo:
				</Text>
				<InputsGroup>
					{voyConVW === TRUE && <SelectExtended name="furgoVW" label="Modelo" options={getModels()} required />}
					{voyConVW === FALSE && <SelectExtended name="brand" label="Marca" options={getBrands()} required />}
					{voyConVW === FALSE && <TextInputExtended name="model" label="Modelo" required />}
					<TextInputExtended
						label="Año"
						name="year"
						validate={(value) => {
							const year = parseInt(value, 10);
							return /^\d{4}$/.test(value) && year >= 1900 && year <= 2026;
						}}
						maxLength={4}
						errorMessage="Año incorrecto (debe estar entre 1900 y 2026)"
						required
					/>
					<PaisMatriculaInput required onBlur={handlePlateBlur} />
				</InputsGroup>

				{furgoInscrita ? (
					<ErrorSection paddingTop={ContainerPadding.static200} align="left">
						Esta furgoneta ya se ha inscrito con otro asistente, si eres un acompañante, selecciona esa opción.
					</ErrorSection>
				) : undefined}
			</Section>
		</div>
	);

	/****************************
	 * HISTORIA
	 ****************************/

	useEffect(() => {
		const unsubscribeMiHistoriaContinue = formService.subscribeToField('miHistoriaContinue', {
			onChange: onMiHistoriaContinue,
		});

		return () => {
			unsubscribeMiHistoriaContinue();
		};
	}, []);

	const [miHistoriaAnswered, setMiHistoriaAnswered] = useState(false);
	const [miHistoriaRequired, setMiHistoriaRequired] = useState(false);

	const onMiHistoriaContinue = (miHistoriaContinue: string | undefined) => {
		checkMiHistoria(miHistoriaContinue, formService.getFieldValue('miHistoria'));
	};

	const checkMiHistoria = (miHistoriaContinue: string | undefined, miHistoriaValue: unknown) => {
		const miHistoria = typeof miHistoriaValue === 'string' && miHistoriaValue !== '';

		if (miHistoriaContinue === TRUE) {
			setMiHistoriaRequired(true);
			if (miHistoria) {
				setMiHistoriaAnswered(true);
				scrollToElement(datosPersonalesRef);
			} else {
				formService.setFieldError('miHistoria', true);
				setMiHistoriaAnswered(false);
			}
		} else {
			formService.setFieldError('miHistoria', false);
			setMiHistoriaRequired(false);
			setMiHistoriaAnswered(true);
			scrollToElement(datosPersonalesRef);
		}
	};

	const sectionMiHistoria = (
		<div ref={miHistoriaRef} hidden={!showMiHistoria()}>
			<Section disableScrollFocus paddingTop={MARGIN_SECTIONS}>
				<Text bold appearance={TokenTextAppearance.headline200}>
					¡Queremos conocer tu historia!
				</Text>
				<Text appearance={TokenTextAppearance.copy200}>
					<b>Cuéntanos tu viaje más épico o esa historia especial que te une a tu furgo:</b> una aventura, un recuerdo
					familiar o una experiencia que no habría sido posible sin ella. Si eres una de las 20 historias seleccionadas,
					te daremos el altavoz que necesitas para inspirar a toda la comunidad. Además, ¡te llevarás un obsequio que os
					acompañará en las siguientes expediciones!
				</Text>

				<ComentarioInput name="miHistoria" required={miHistoriaRequired} />
				<CtasRadio
					name="miHistoriaContinue"
					options={[
						{ text: 'Continuar', value: TRUE, emphasis: 'primary' },
						{ text: 'Ahora no', value: FALSE },
					]}
					width={CTA_WIDTH + 'px'}
					onClick={() => {
						const miHistoriaContinue = formService.getFieldValue('miHistoriaContinue');
						const miHistoria = formService.getFieldValue('miHistoria');
						checkMiHistoria(miHistoriaContinue, miHistoria);
					}}
				/>
			</Section>
		</div>
	);

	/****************************
	 * DATOS PERSONALES
	 ****************************/

	const showDniInput = () => {
		return soyMiembro !== TRUE; // No usar soyMiembro === FALSE
	};

	const sectionDatosPersonales = (
		<div ref={datosPersonalesRef} hidden={!showDatosPersonales()}>
			<Section disableScrollFocus paddingTop={MARGIN_SECTIONS}>
				<Text bold appearance={TokenTextAppearance.headline200}>
					Tus datos personales
				</Text>
				{foundMiembro && (
					<Text appearance={TokenTextAppearance.copy200}>
						Estos son los datos que tenemos de ti. Revísalos y ajústalos si hace falta.
					</Text>
				)}
				<InputsGroup>
					<TratoNombreInput />
					<LettersInput name={FIELD_NAME.SURNAME_1} label="Primer apellido" required />
					{showDniInput() && <DniInput types={['DNI', 'NIE', 'PASAPORTE', 'OTRO']} required />}
					<EmailInput required />
					<TelefonoInput required />
					<FechaInput name={FIELD_NAME.BIRTHDAY} label="Fecha nacimiento" required onlyAdults />
					<CodigoPostalInput required />
					<LettersInput name={FIELD_NAME.CITY} label="Ciudad" required />
					<LettersInput name={FIELD_NAME.PROVINCE} label="Provincia" required />
					<SelectExtended name={FIELD_NAME.COUNTRY} label="País" options={getCountries()} value="ES" required />
				</InputsGroup>
			</Section>

			<ConsentimientosSection
				paddingTop={MARGIN_SECTIONS}
				paddingBottom={MARGIN_SECTIONS}
				showComunicacionesComerciales
				showPerfilado
			/>

			<EnvioSection formData={getFormData} showInfoRequiredFields alwaysSuccess errorMessage={errorEnvioMessage} />
		</div>
	);

	const sectionLegales = <LegalesSection paddingTop={MARGIN_SECTIONS} />;

	/****************************
	 * STYLES
	 ****************************/

	const styles = (
		<style>
			{`
		.oneform .cta.secondary button,
		.oneform .cta.secondary a
		{ 
			background: rgb(255, 255, 255); 
			border: 1px solid rgb(194, 202, 207);
			color: rgb(0, 30, 80);
		} 
		.oneform .cta.primary button, 
		.oneform .cta button:active,
		.oneform .cta.primary a, 
		.oneform .cta a:active
		{ 
			background: rgb(246, 156, 34); 
			border: 1px solid rgb(246, 156, 34);
			color: rgb(0, 30, 80);
		}
		.oneform .cta button:focus,
		.oneform .cta a:focus
		{
			background: rgb(246, 156, 34) !important;
			border: 1px solid rgb(246, 156, 34) !important;
		}
		.oneform .cta button:hover,
		.oneform .cta a:hover
		{ 
			background: rgb(247, 178, 82) !important; 
			border: 1px solid rgb(247, 178, 82) !important;
		}
		.oneform .card[aria-selected=true] {
			box-shadow: rgb(246, 156, 34) 0px 0px 0px 2px inset;
		}
		.oneform .card[aria-selected=true] svg {
			color: rgb(246, 156, 34);
		}
	`}
		</style>
	);

	/****************************
	 * THANKYOU
	 ****************************/

	const CustomThankyou = () => {
		const ctasThankyouScreen = [
			<div className="cta primary">
				<CTA tag="a" href={CAMPING_URL} emphasis="primary" target="_blank">
					Reserva en el camping
				</CTA>
			</div>,
		];
		return (
			<>
				{styles}
				<ThankyouScreen
					align="center"
					title={
						<>
							<Text appearance={TokenTextAppearance.headline300} textAlign={TextAlignment.center} bold>
								Inscripción confirmada.
								<br />
								¡Nos vemos en la Concentración FurgoVolkswagen 2026!
							</Text>
						</>
					}
					text={
						<>
							<Text appearance={TokenTextAppearance.copy100} textAlign={TextAlignment.center}>
								Pronto recibirás un correo electrónico de confirmación con el{' '}
								<b>código QR para acceder a la concentración</b>. ¡Guárdalo bien!
								<br />
								<br />
								Puedes hacer la reserva de tu parcela o bungalow desde la web del camping.
							</Text>
						</>
					}
					ctas={ctasThankyouScreen}
					legal={<LegalesSection />}
				/>
			</>
		);
	};

	useEffect(() => {
		setAppThankyouScreen(<CustomThankyou />);
	}, []);

	/****************************
	 * FORM
	 ****************************/

	return (
		<>
			{styles}
			{sectionHeader}
			{sectionVoyConFurgo}
			{sectionVoyConVW}
			{sectionSoyTitular}
			{sectionSoyMiembro}
			{sectionDatosMiembro}
			{sectionSeleccionFurgo}
			{sectionDatosFurgo}
			{sectionMiHistoria}
			{sectionDatosPersonales}
			{sectionLegales}
		</>
	);
};
